import React, { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { STAGES, stageMeta, gbp } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { TeamAvatar } from "@/components/TeamAvatar";
import { FollowUpBadge } from "@/components/FollowUpBadge";

export default function Pipeline() {
  const [enquiries, setEnquiries] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const load = () => {
    api.enquiries().then(setEnquiries);
    api.team().then(setTeam);
  };
  useEffect(() => { load(); }, []);

  const memberById = (id) => team.find((m) => m.id === id);
  const grouped = useMemo(() => {
    const g = {};
    STAGES.forEach((s) => (g[s.key] = []));
    enquiries.forEach((e) => { (g[e.stage] || (g[e.stage] = [])).push(e); });
    return g;
  }, [enquiries]);

  const save = async (payload) => {
    if (editing) {
      await api.updateEnquiry(editing.id, payload);
      toast.success("Enquiry updated");
    } else {
      await api.createEnquiry(payload);
      toast.success("Enquiry created");
    }
    setOpen(false); setEditing(null); load();
  };

  const moveStage = async (id, stage) => {
    setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, stage } : e)));
    await api.updateEnquiry(id, { stage });
    toast.success(`Moved to ${stageMeta(stage).label}`);
  };

  const onDrop = (stageKey) => {
    if (dragId) {
      const cur = enquiries.find((e) => e.id === dragId);
      if (cur && cur.stage !== stageKey) moveStage(dragId, stageKey);
    }
    setDragId(null); setDragOver(null);
  };

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Pipeline</h1>
          <p className="mt-1 text-sm text-slate-500">Drag cards between stages or use the menu to move them.</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-enquiry-button" style={{ backgroundColor: "hsl(var(--brand))" }} className="rounded-full text-white">
          <Plus className="mr-1 h-4 w-4" /> New enquiry
        </Button>
      </div>

      <div className="kanban-scroll flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4">
        {STAGES.map((s) => {
          const items = grouped[s.key] || [];
          const colValue = items.reduce((a, e) => a + (Number(e.deal_value) || 0), 0);
          return (
            <div
              key={s.key}
              data-testid={`kanban-column-${s.key}`}
              onDragOver={(ev) => { ev.preventDefault(); setDragOver(s.key); }}
              onDragLeave={() => setDragOver((d) => (d === s.key ? null : d))}
              onDrop={() => onDrop(s.key)}
              className={`flex w-[82vw] max-w-[300px] shrink-0 snap-start flex-col rounded-2xl border sm:w-[290px] ${s.col} ${dragOver === s.key ? "ring-2 ring-offset-2" : ""}`}
              style={dragOver === s.key ? { boxShadow: "0 0 0 2px hsl(var(--brand-accent))" } : {}}
            >
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                  <span className="text-sm font-bold text-slate-800">{s.label}</span>
                  <span className="rounded-full bg-white/70 px-2 text-xs font-semibold text-slate-600">{items.length}</span>
                </div>
                <span className="font-mono-fin text-[11px] text-slate-500">{gbp(colValue)}</span>
              </div>
              <div className="flex flex-1 flex-col gap-3 px-3 pb-3">
                {items.map((e) => (
                  <div
                    key={e.id}
                    draggable
                    onDragStart={() => setDragId(e.id)}
                    onDragEnd={() => setDragId(null)}
                    onClick={() => { setEditing(e); setOpen(true); }}
                    data-testid={`enquiry-card-${e.id}`}
                    className={`cursor-pointer rounded-xl border border-white bg-white p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${dragId === e.id ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-bold text-slate-900">{e.business_name}</p>
                      <TeamAvatar member={memberById(e.assignee_id)} />
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <p className="text-xs text-slate-500">{e.contact_name}</p>
                      {e.source === "companies_house" && (
                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-700" data-testid={`lead-badge-${e.id}`}>CH Lead</span>
                      )}
                      {e.source === "website" && (
                        <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-sky-700" data-testid={`web-badge-${e.id}`}>Website</span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(e.services || []).slice(0, 2).map((sv) => (
                        <span key={sv} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{sv}</span>
                      ))}
                      {(e.services || []).length > 2 && <span className="text-[10px] text-slate-400">+{e.services.length - 2}</span>}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="font-mono-fin text-xs font-semibold text-slate-700">{gbp(e.deal_value)}</span>
                      <FollowUpBadge date={e.follow_up_date} />
                    </div>
                    <div className="mt-2 flex gap-1 border-t border-slate-100 pt-2" onClick={(ev) => ev.stopPropagation()}>
                      <select
                        value={e.stage}
                        onChange={(ev) => moveStage(e.id, ev.target.value)}
                        data-testid={`move-stage-${e.id}`}
                        className="w-full rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-medium text-slate-600 outline-none"
                      >
                        {STAGES.map((st) => <option key={st.key} value={st.key}>{st.label}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 py-6 text-center text-[11px] text-slate-400">Drop here</div>}
              </div>
            </div>
          );
        })}
      </div>

      <EnquiryDialog open={open} onOpenChange={setOpen} onSave={save} team={team} initial={editing} />
    </div>
  );
}
